import type {PluginData} from "../core/PluginData.ts";
import {Label} from "@ui5/webcomponents-react";
import server from "../core/Server.ts";
import {useState} from "react";
import type {PublishState} from "../core/PublishState.ts";
import type {SentViaOption} from "../sentVia/SentVia.tsx";

/**
 * Represents the properties required for a Tag component.
 */
interface TagProps {
    /**
     * The email address of the plugin recipient.
     */
    mailAddress: string,
    /**
     * The plugin data.
     */
    plugin: PluginData,
    /**
     * The ID of the current requested plugin.
     */
    requestedPluginId?: string;
    /**
     * Whether the plugin is sent via email or as a download URL.
     */
    sentVia: SentViaOption,
    /**
     * The tag to display.
     */
    tag: string,
    /**
     * The date (YYYY-MM-DD) until the download URL is valid. Only relevant if sentVia is 'URL'.
     */
    validUntil?: string,
    /**
     * The function to call when a plugin is requested.
     */
    onPluginRequested: (pluginId: string | undefined) => void;
    onError: (state: PublishState) => void;
}

/**
 * Represents a Tag component.
 * @param props - The properties for the Tag component.
 * @returns The rendered Tag component.
 */
export function Tag(props: TagProps) {
    const [inProgress, setInProgress] = useState<true | undefined>()
    return <div className="sc_tag">
        <Label>{props.tag}</Label>
        {!inProgress && !props.requestedPluginId
            ? <a
                onClick={async () => {
                    setInProgress(true);
                    props.onPluginRequested(props.plugin.id);
                    const state = props.sentVia === 'URL' && props.validUntil
                        ? await server.publishPluginUrl({
                            mailAddress: props.mailAddress,
                            tag: props.tag,
                            id: props.plugin.id,
                            validUntil: props.validUntil
                        })
                        : await server.publishPlugin({
                            mailAddress: props.mailAddress,
                            tag: props.tag,
                            id: props.plugin.id
                        });
                    setInProgress(undefined);
                    props.onPluginRequested(undefined);
                    if (!state.success) {
                        props.onError(state);
                    }
                }}
                href='#'
            >request plugin</a>
            : inProgress && props.requestedPluginId === props.plugin.id
                ? <Label>in progress...</Label>
                : <Label>request plugin</Label>
        }
    </div>;
}
